import { styled } from "@mui/material/styles";
import { useQuery } from "@tanstack/react-query";
import { useIntl } from "react-intl";

import {
  SelectBoxBase,
  type Option,
} from "@/common/components/forms/select-box/SelectBox";
import Countries from "@/shared/services/Countries.service";

import { countriesQuery, type Country } from "./signup.api";

type Props = {
  value: string;
  onChange: (countryCode: string) => void;
};

const CountrySelector = ({ onChange, value }: Props): React.ReactElement => {
  const { formatMessage } = useIntl();
  const countries = useQuery(countriesQuery());

  const label = formatMessage({ id: "common.country" });

  return (
    <SelectBoxBase<string>
      fetchError={countries.isError ? "common.error" : undefined}
      fullWidth
      label={label}
      loading={countries.isLoading}
      name="countryCode"
      onChange={onChange}
      onRetry={countries.refetch}
      options={toOptions(countries.data ?? [])}
      placeholder={label}
      value={value}
    />
  );
};

const toOptions = (countries: Country[]): Option<string>[] =>
  countries
    .slice()
    .sort((a, b) => Number(b.fullSupport) - Number(a.fullSupport))
    .map((country) => ({
      value: country.code,
      label: (
        <CountryLabel>
          <FlagSpan>{Countries.countryToFlag(country.code)}</FlagSpan>
          {country.name}
        </CountryLabel>
      ),
    }));

const CountryLabel = styled("span")({
  display: "inline-flex",
  alignItems: "center",
});

const FlagSpan = styled("span")({
  width: 32,
  flexShrink: 0,
});

export { CountrySelector };
